import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, CalendarIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline'; // Assuming usage of Heroicons
import RangeDatePicker from '../Layout/DatePicker.tsx'; // Import RangeDatePicker
import 'react-datepicker/dist/react-datepicker.css'; // Import base CSS
import '../Layout/DatePicker.css'; // Import custom styles (adjust path if needed)

interface ScheduleReportModalProps {
  onClose: () => void;
  reportTitle: string; // Pass the report title to pre-select or display
}

// Basic styles - consider using a shared style system or CSS modules
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)', // Darker backdrop
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px', // Added padding for responsiveness
};

const modalContentStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px', // Larger radius
  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // Softer shadow
  width: '90%',
  maxWidth: '600px', // Slightly wider max width
  overflow: 'hidden', // To contain border radius
  display: 'flex', // Added for flex layout
  flexDirection: 'column', // Added for flex layout
  maxHeight: '90vh', // Limit height
};

const modalHeaderStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb', // light gray border
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

const modalBodyStyle: React.CSSProperties = {
    padding: '24px 32px',
    overflowY: 'auto', // Make body scrollable if content exceeds height
};

const modalFooterStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb', // light gray border
    display: 'flex',
    justifyContent: 'flex-end', // Align buttons right by default
    alignItems: 'center',
    gap: '12px', // Space between buttons
    backgroundColor: '#f9fafb', // Light background for footer
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px', // Adjusted margin
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151', // Medium gray
};

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px', // Slightly more padding
  border: '1px solid #d1d5db', // Gray border
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box', // Include padding and border in width
  backgroundColor: 'white', // Ensure white background
  color: '#111827', // Dark text
};

const selectContainerStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
};

// Note: Select requires appearance: none and background SVG for custom arrow
const selectStyle: React.CSSProperties = {
  ...inputBaseStyle,
  appearance: 'none',
  paddingRight: '36px', // Ensure space for the arrow
};

const iconBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '#6b7280', // Gray icon
    width: '18px', // Consistent icon size
    height: '18px', // Consistent icon size
};

const selectIconStyle: React.CSSProperties = {
    ...iconBaseStyle,
    right: '12px',
};

const dateInputContainerStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
};

const dateInputStyle: React.CSSProperties = {
  ...inputBaseStyle,
  paddingRight: '36px', // Space for calendar icon
};

const dateIconStyle: React.CSSProperties = {
  ...iconBaseStyle,
  right: '12px',
};

const buttonBaseStyle: React.CSSProperties = {
    padding: '10px 20px', // Consistent padding
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: 500,
    lineHeight: '1.5',
    border: '1px solid transparent', // Base border
    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out', // Smooth transition
  };

// Primary Button (Export style)
const primaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: '#1f2937', // Dark background (like Export)
    color: 'white',
    borderColor: '#1f2937', // Ensure border matches
};

// Secondary Button (Back/Cancel style)
const secondaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: 'white',
    color: '#374151', // Medium gray text
    border: '1px solid #d1d5db', // Light gray border
};

// Add Recipient Button (Subtle style)
const addRecipientButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    padding: '8px 16px', // Slightly smaller padding
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    marginTop: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px', // Icon gap
};

const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center', // Center vertically
    gap: '10px', // Consistent gap
    marginTop: '16px', // Top margin
    marginBottom: '24px', // Bottom margin
    padding: '10px', // Add some padding
    borderRadius: '6px', // Rounded corners
    // border: '1px solid #e5e7eb', // Optional: add border like ExportConfig options
    cursor: 'pointer', // Make area clickable
};

const checkboxStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    accentColor: '#1f2937', // Style the check color like ExportConfig
    flexShrink: 0, // Prevent shrinking
};

const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: '#6b7280',
    borderRadius: '50%',
    display: 'flex',
    transition: 'background-color 0.2s ease-in-out', // Hover effect
};

const ScheduleReportModal: React.FC<ScheduleReportModalProps> = ({ onClose, reportTitle }) => {
  // TODO: Add state for form fields (reportName, frequency, format, recipients, includeAttachment)
  const [recipients, setRecipients] = React.useState<string[]>(['']); // Start with one empty input
  const modalRef = React.useRef<HTMLDivElement>(null); // Ref for clicking outside
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

   // Handle clicks outside the modal (from ExportConfigModal)
   React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);


  const handleAddRecipient = () => {
    setRecipients([...recipients, '']);
  };

  const handleRecipientChange = (index: number, value: string) => {
    const newRecipients = [...recipients];
    newRecipients[index] = value;
    setRecipients(newRecipients);
  };

  // Mock data - replace with actual data fetching or props
  const availableReports = [
    'Payout Summary',
    'Revenue Report',
    'Tax Report',
    'Partner Performance',
    'Financial Statement',
    'Transaction Reconciliation',
    'Risk Assessment',
    'Partner Settlement',
    'Product Performance',
    'Audit Log',
  ];
  const frequencies = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'On Demand'];
  const formats = ['PDF', 'Excel', 'CSV'];

  return (
    <div style={modalOverlayStyle} /* onClick={onClose} - Removed overlay close, handled by useEffect */>
      <div style={modalContentStyle} ref={modalRef} /* onClick={(e) => e.stopPropagation()} - No longer needed with ref */>
        {/* Modal Header */}
        <div style={modalHeaderStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Schedule Report</h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
              Set up a recurring schedule for report generation and delivery.
            </p>
          </div>
          <button
            onClick={onClose}
            style={closeButtonStyle}
            aria-label="Close modal"
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'} // Hover effect
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'} // Hover effect
          >
            <XMarkIcon style={{ width: '20px', height: '20px' }} /> {/* Slightly smaller close icon */}
          </button>
        </div>

        {/* Modal Body */}
        <div style={modalBodyStyle}>
          <form onSubmit={(e) => e.preventDefault()}> {/* Handle form submission */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}> {/* Increased gap */}
              {/* Report Dropdown */}
              <div>
                <label htmlFor="report" style={labelStyle}>Report</label>
                <div style={selectContainerStyle}>
                  <select id="report" style={selectStyle} defaultValue={reportTitle || ""}>
                    <option value="" disabled>Select a report</option>
                    {availableReports.map(r => <option key={r} value={r}>{r}</option>)}
                    {/* Add more options based on available reports */}
                  </select>
                  <ChevronUpDownIcon style={selectIconStyle} />
                </div>
              </div>

              {/* Report Name */}
              <div>
                <label htmlFor="reportName" style={labelStyle}>Report Name</label>
                <input type="text" id="reportName" placeholder="Enter a name for this scheduled report" style={inputBaseStyle} />
              </div>

              {/* Frequency */}
              <div>
                <label htmlFor="frequency" style={labelStyle}>Frequency</label>
                 <div style={selectContainerStyle}>
                   <select id="frequency" style={selectStyle} defaultValue="Weekly">
                      {frequencies.map(f => <option key={f} value={f}>{f}</option>)}
                   </select>
                   <ChevronUpDownIcon style={selectIconStyle} />
                 </div>
              </div>

              {/* Start Date -> Date Range */}
              <div>
                <label htmlFor="dateRange" style={labelStyle}>Date Range</label>
                <RangeDatePicker
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    onChange={setDateRange}
                />
              </div>

               {/* Format */}
               <div>
                <label htmlFor="format" style={labelStyle}>Format</label>
                <div style={selectContainerStyle}>
                  <select id="format" style={selectStyle} defaultValue="PDF">
                      {formats.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                   <ChevronUpDownIcon style={selectIconStyle} />
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label htmlFor="recipients" style={labelStyle}>Recipients</label>
                {recipients.map((recipient, index) => (
                  <input
                    key={index}
                    type="email"
                    placeholder="Email address"
                    value={recipient}
                    onChange={(e) => handleRecipientChange(index, e.target.value)}
                    style={{ ...inputBaseStyle, marginBottom: index < recipients.length - 1 ? '10px' : 0 }} // Increased margin between inputs
                  />
                 ))}
                <button type="button" onClick={handleAddRecipient} style={addRecipientButtonStyle}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  {/* Optional: Add a PlusIcon here */}
                  + Add Recipient
                </button>
              </div>
            </div>

            {/* Include Attachment Checkbox */}
            <label htmlFor="includeAttachment" style={checkboxContainerStyle}> {/* Use label for clickability */}
              <input type="checkbox" id="includeAttachment" defaultChecked style={checkboxStyle} />
              <span style={{ fontSize: '14px', color: '#374151' }}> {/* Span for better control */}
                Include report as attachment
              </span>
            </label>
          </form>
        </div>

        {/* Modal Footer */}
        <div style={modalFooterStyle}>
          <button
            type="button"
            onClick={onClose}
            style={secondaryButtonStyle}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'} // Hover effect
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'} // Hover effect
            >
                Cancel
          </button>
          <button
             type="submit" // Assuming this triggers form submission eventually
             style={primaryButtonStyle}
             onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'} // Hover effect
             onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1f2937'} // Hover effect
            >
             Schedule Report
            </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleReportModal; 